<template>
  <organism-server-management
    :equipmentList="equipmentList"
    :tagsList="tagsList"
    :selectedEquipmentDetail="selectedEquipmentDetail"
    :graphStatCpuData="graphStatCpuData"
    :graphStatMemoryData="graphStatMemoryData"
    :statStorageData="statStorageData"
    :resourceData="resourceData"
    @selectedId="routerToEquipmentIdPath"
    @datetimeParams="setDatetimeParams"
    @updateCurrentEquipmentTagList="updateCurrentEquipmentTagList"
    @updateTagsFromTripleDot="updateTagsFromTripleDot"
    @applyPolicy="applyPolicy"
    @getEquipmentDetail="routerToEquipmentIdPath"
  ></organism-server-management>
</template>

<script>
import { DateTime } from "luxon"
import OrganismServerManagement from "@/components/organisms/OrganismServerManagement.vue"
import {
  getEquipment,
  getEquipmentById,
  getTags,
  getGraphCpuStatById,
  getGraphMemoryStatById,
  getStorageStatById,
  updateTagListToSingleEquipment,
  updateTagListToMultipleEquipment,
  updatePolicyToSingleEquipment,
} from "@/api/api-module"
import mixins from "@/mixins"
import mixinsDateTime from "@/mixins/mixinsDatetime"
export default {
  name: "ServerManagement",
  components: { OrganismServerManagement },
  mixins: [mixins, mixinsDateTime],
  data() {
    return {
      isLoading: true,
      equipmentList: [],
      tagsList: [],
      resourceData: [],
      selectedEquipmentDetail: null,
      graphStatCpuData: null,
      graphStatMemoryData: null,
      statStorageData: null,
      dateTimeParams: {
        from: "",
        to: "",
      },
      unitList: {
        cpu: [],
        memory: [],
        storage: [],
      },
      resourceTypePolicy: ["CPU", "Memory", "Storage"],
    }
  },
  computed: {
    currentEquipmentId() {
      return this.$route.params?.server_id
    },
  },
  watch: {
    currentEquipmentId: {
      async handler() {
        await this.conditionSelectEquipment()
      },
    },
  },
  async created() {
    this.dateTimeParams = {
      from: this.removeTimeZone(DateTime.now().minus({ days: 1 }).toISO()),
      to: this.removeTimeZone(DateTime.now().toISO()),
    }
  },
  async mounted() {
    await this.getBaseDataFromApi()
    await this.conditionSelectEquipment()
  },
  methods: {
    async getBaseDataFromApi() {
      await Promise.all([this.getEquipmentList(), this.getTagsList()])
    },

    async conditionSelectEquipment() {
      this.showLoading(true)
      const selectedEquipmentId = this.currentEquipmentId
      const isSelectedIdInEquipmentList = this.equipmentList.find(
        (equipment) => equipment.id === selectedEquipmentId
      )
      if (selectedEquipmentId && isSelectedIdInEquipmentList) {
        await this.getEquipmentDetailByIdFromApi(selectedEquipmentId)
      } else if (
        this.equipmentList.length &&
        selectedEquipmentId !== this.equipmentList[0].id
      ) {
        this.routerToEquipmentIdPath(this.equipmentList[0].id)
      } else if (selectedEquipmentId) {
        this.$router.push("/server-management/server-management/")
      }
    },

    async routerToEquipmentIdPath(id) {
      if (!id && this.currentEquipmentId) {
        id = this.currentEquipmentId
      }
      this.$router.push(`/server-management/server-management/${id}`)
    },

    async getEquipmentList() {
      this.showLoading(true)
      await getEquipment()
        .then((response) => {
          this.equipmentList = response.map((equipment) => {
            return {
              id: equipment.equipmentId,
              name: equipment.hostname,
              ipAddress: equipment.ipAddress,
              description: equipment.ipAddress,
              tag: equipment.tags,
              isChecked: false,
              isSelected: false,
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getTagsList() {
      this.showLoading(true)
      await getTags()
        .then((response) => {
          if (response) {
            this.tagsList = response
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getEquipmentDetailByIdFromApi(id) {
      if (!id && this.currentEquipmentId) {
        id = this.currentEquipmentId
      }

      this.equipmentList.forEach((equipment) => (equipment.isSelected = false))
      const targetIndex = this.equipmentList.findIndex(
        (equipment) => equipment.id === id
      )
      this.equipmentList[targetIndex].isSelected = true

      await this.getEquipmentDetailById(id)
    },

    async getEquipmentDetailById(id) {
      await Promise.all([
        this.getEquipmentDetailByIdApi(id),
        this.getGraphStatDataApi(id),
      ])
    },

    async getEquipmentDetailByIdApi(id) {
      this.showLoading(true)
      this.resourceData = []
      this.selectedEquipmentDetail = null

      await getEquipmentById(id)
        .then(async (response) => {
          if (response) {
            this.selectedEquipmentDetail = {
              id: response.equipmentId,
              name: response.hostname,
              description: response.description,
              ipAddress: response.ipAddress,
              tag: response.tags,
              isChecked: false,
              isSelected: true,
              isEnable: response.enableMonitoring,
              policy: response.policy,
              isCustomPolicy: response.usingCustomMonitoringThreshold,
            }

            this.resourceData = response.policy.policyValue.map((policy) => {
              return {
                resource: policy.policyResourceTypeName,
                warning: policy.policyResourceWarningThreshold,
                critical: policy.policyResourceCriticalThreshold,
                unit: {
                  id: policy.policyResourceUnitId,
                  name: policy.policyResourceUnit,
                },
                isEnabled: policy.policyResourceEnable,
              }
            })

            if (this.resourceData.length !== 3) {
              this.resourceTypePolicy.forEach((type) => {
                const resource = this.resourceData.find(
                  (item) => item.resource === type
                )
                if (!resource) {
                  this.resourceData.push({
                    resource: type,
                    warning: "",
                    critical: "",
                    unit: { id: "", name: "" },
                    isEnabled: false,
                  })
                }
              })
            }

            this.resourceData.sort((a, b) => {
              return a.resource.toLowerCase() < b.resource.toLowerCase()
                ? -1
                : 1
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getGraphStatDataApi(id) {
      await Promise.all([
        getGraphCpuStatById(id, this.dateTimeParams).then((response) => {
          this.graphStatCpuData = response || null
        }),
        getGraphMemoryStatById(id, this.dateTimeParams).then((response) => {
          this.graphStatMemoryData = response || null
        }),
        getStorageStatById(id, this.dateTimeParams).then((response) => {
          this.statStorageData = response?.disks.length ? response : null
        }),
      ])
    },

    async setDatetimeParams(dateTime) {
      this.dateTimeParams = {
        from: this.removeTimeZone(dateTime.start),
        to: this.removeTimeZone(dateTime.end),
      }
      await this.getGraphStatDataApi(this.selectedEquipmentDetail.id)
    },

    async updateCurrentEquipmentTagList(tagList) {
      if (this.selectedEquipmentDetail.tag !== tagList) {
        const response = await updateTagListToSingleEquipment(
          this.selectedEquipmentDetail.id,
          tagList
        )
        if (response) {
          this.toastSuccess("Tag updated successfully")
          this.selectedEquipmentDetail.tag = tagList
          await this.getBaseDataFromApi()
          this.equipmentList.forEach(
            (equipment) => (equipment.isSelected = false)
          )
          const targetIndex = this.equipmentList.findIndex(
            (equipment) => equipment.id === this.currentEquipmentId
          )
          this.equipmentList[targetIndex].isSelected = true
        } else this.toastError("Tag update failed")
        await this.getTagsList()
      }
    },

    async updateTagsFromTripleDot(request, equipmentId) {
      this.showLoading(true)
      await updateTagListToMultipleEquipment(
        request.equipmentIds,
        request.tags,
        request.overwrite
      )
        .then((response) => {
          if (response) {
            this.toastSuccess("Tag updated successfully")
            if (equipmentId) this.getEquipmentDetailById(equipmentId)
            this.getBaseDataFromApi()
          } else this.toastError("Tag update failed")
        })
        .finally(() => this.showLoading(false))
    },

    async applyPolicy(policy) {
      this.showLoading(true)
      await updatePolicyToSingleEquipment(this.currentEquipmentId, policy)
        .then((response) => {
          if (response.status === "ok") {
            this.toastSuccess("Equipment policy updated successfully")
            this.getEquipmentDetailByIdApi(this.currentEquipmentId)
          } else this.toastError("Equipment policy update failed")
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style></style>
